
import React from 'react';
import {
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ChevronDown, ChevronUp, AlertCircle, AlertOctagon } from "lucide-react";

interface LeagueTableHeaderProps {
  onSort: (column: string) => void;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
}

const LeagueTableHeader: React.FC<LeagueTableHeaderProps> = ({
  onSort,
  sortColumn,
  sortDirection
}) => {
  const renderSortIcon = (column: string) => {
    if (sortColumn !== column) return null;
    // Flip the arrow direction: up for desc (highest first), down for asc (lowest first)
    return sortDirection === 'asc' ? <ChevronDown className="ml-1 h-4 w-4" /> : <ChevronUp className="ml-1 h-4 w-4" />;
  };

  const columns = [
    { key: 'team', label: 'Team', description: 'Team Name', sortable: false },
    { key: 'form', label: 'Form', description: 'Last 6 matches (W = Win, D = Draw, L = Loss) - Click to sort by form points', sortable: true },
    { key: 'played', label: 'P', description: 'Matches Played', sortable: true },
    { key: 'wins', label: 'W', description: 'Wins', sortable: true },
    { key: 'draws', label: 'D', description: 'Draws', sortable: true },
    { key: 'losses', label: 'L', description: 'Losses', sortable: true },
    { key: 'goalsFor', label: 'F', description: 'Goals Scored', sortable: true },
    { key: 'goalsAgainst', label: 'A', description: 'Goals Conceded', sortable: true },
    { key: 'goalDifference', label: 'GD', description: 'Goal Difference (Goals Scored - Goals Conceded)', sortable: true },
    { key: 'points', label: 'PTS', description: 'Points (3 for a win, 1 for a draw)', sortable: true },
    { key: 'ppg', label: 'PPG', description: 'Points Per Game', sortable: true },
    { key: 'cleanSheets', label: 'CS', description: 'Clean Sheets (Matches without conceding)', sortable: true },
    { key: 'btts', label: 'BTTS', description: 'Both Teams To Score', sortable: true },
    { key: 'over15', label: 'O1.5', description: 'Matches with Over 1.5 Goals', sortable: true },
    { key: 'over25', label: 'O2.5', description: 'Matches with Over 2.5 Goals', sortable: true },
    { 
      key: 'cards', 
      label: (
        <div className="flex items-center gap-1">
          <AlertCircle className="h-4 w-4 text-yellow-500" />
          <span>/</span>
          <AlertOctagon className="h-4 w-4 text-red-500" />
        </div>
      ),
      description: 'Yellow Cards / Red Cards per game',
      sortable: true 
    },
  ];

  return (
    <TooltipProvider>
      <TableHeader>
        <TableRow className="border-b border-gray-200">
          {columns.map(({ key, label, description, sortable }) => (
            <TableHead key={key} className="font-medium text-gray-900 text-center p-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  {sortable ? (
                    <Button
                      variant={sortColumn === key ? "default" : "ghost"}
                      onClick={() => onSort(key)}
                      className={`px-2 ${sortColumn === key ? 'bg-blue-500 text-white hover:bg-blue-600' : 'hover:bg-gray-100'} font-medium`}
                    >
                      <span className="flex items-center">
                        {label}
                        {renderSortIcon(key)}
                      </span>
                    </Button>
                  ) : (
                    <div className="cursor-help">{label}</div>
                  )}
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm">{description}</p>
                </TooltipContent>
              </Tooltip>
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
    </TooltipProvider>
  );
};

export default LeagueTableHeader;
